import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Home, MapPin, BedDouble, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { API } from "@/App";

const TenUResLogo = ({ size = "text-4xl" }) => (
  <span className={`tenures-logo ${size}`}>
    Ten<span className="tenures-ur font-extrabold">UR</span>es
  </span>
);

const POSTCODES = {
  "London": ["E", "N", "NW", "SE", "SW", "W", "WC", "EC"],
  "Birmingham": ["B"]
};

const PROPERTY_TYPES = [
  { value: "flat", label: "Flat" },
  { value: "house", label: "House" },
  { value: "maisonette", label: "Maisonette" },
  { value: "bungalow", label: "Bungalow" }
];

const BEDROOM_OPTIONS = [1, 2, 3, 4, 5, 6];

export default function Onboarding({ user, setUser, checkAuth }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [formData, setFormData] = useState({
    tenancy_start_date: "",
    property_type: "",
    current_bedrooms: "",
    needed_bedrooms: "",
    postcode_district: ""
  });

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    setFormData({
      ...formData,
      tenancy_start_date: selectedDate ? selectedDate.toISOString().split('T')[0] : ""
    });
    setCalendarOpen(false);
  };

  const handleSubmit = async () => {
    if (!formData.tenancy_start_date || !formData.property_type || 
        !formData.current_bedrooms || !formData.needed_bedrooms || !formData.postcode_district) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/onboarding`, {
        ...formData,
        current_bedrooms: parseInt(formData.current_bedrooms),
        needed_bedrooms: parseInt(formData.needed_bedrooms)
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });

      toast.success("Welcome to your TenURes passport!");
      
      if (checkAuth) {
        await checkAuth();
      }
      
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to save. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && !formData.tenancy_start_date) {
      toast.error("Please select your tenancy start date");
      return;
    }
    if (step === 2 && !formData.property_type) {
      toast.error("Please select your property type");
      return;
    }
    if (step === 3 && (!formData.current_bedrooms || !formData.needed_bedrooms)) {
      toast.error("Please select bedroom counts");
      return;
    }
    setStep(step + 1);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg"
      >
        <Card className="bg-white border-gray-200 shadow-lg">
          <CardHeader className="text-center pb-4">
            <div className="mb-4">
              <TenUResLogo size="text-3xl" />
            </div>
            <CardTitle className="text-2xl font-bold text-[#111827]">
              Set Up Your Passport
            </CardTitle>
            <CardDescription className="text-gray-500">
              Tell us about your home so we can help you protect what's <span className="tenures-ur font-semibold">UR</span>s
            </CardDescription>
            
            {/* Progress indicators */}
            <div className="flex justify-center gap-2 mt-4">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    s <= step ? 'bg-[#05D38A]' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Step 1: Tenancy Start Date */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#05D38A]/10 flex items-center justify-center">
                    <CalendarIcon className="w-5 h-5 text-[#05D38A]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#111827]">When did your tenancy start?</h3>
                    <p className="text-sm text-gray-500">This helps calculate your housing rights</p>
                  </div>
                </div>
                
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-white border-gray-300 hover:bg-gray-50 text-[#111827]"
                      data-testid="date-picker-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-gray-400" />
                      {date ? format(date, "PPP") : "Select tenancy start date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-gray-200" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={handleDateSelect}
                      disabled={(date) => date > new Date()}
                      initialFocus
                      className="bg-white"
                      data-testid="calendar"
                    />
                  </PopoverContent>
                </Popover>
              </motion.div>
            )}

            {/* Step 2: Property Type */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#05D38A]/10 flex items-center justify-center">
                    <Home className="w-5 h-5 text-[#05D38A]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#111827]">What type of property do you live in?</h3>
                    <p className="text-sm text-gray-500">This affects your Right to Buy calculation</p>
                  </div>
                </div>
                
                <Select
                  value={formData.property_type}
                  onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                >
                  <SelectTrigger className="bg-white border-gray-300 text-[#111827]" data-testid="property-type-select">
                    <SelectValue placeholder="Select property type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {PROPERTY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value} className="text-[#111827]">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </motion.div>
            )}

            {/* Step 3: Bedrooms */}
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#05D38A]/10 flex items-center justify-center">
                    <BedDouble className="w-5 h-5 text-[#05D38A]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#111827]">How many bedrooms?</h3>
                    <p className="text-sm text-gray-500">This helps identify downsizing opportunities</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#111827]">Current bedrooms</Label>
                    <Select
                      value={formData.current_bedrooms}
                      onValueChange={(value) => setFormData({ ...formData, current_bedrooms: value })}
                    >
                      <SelectTrigger className="bg-white border-gray-300 text-[#111827]" data-testid="current-bedrooms-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {BEDROOM_OPTIONS.map((num) => (
                          <SelectItem key={num} value={num.toString()} className="text-[#111827]">
                            {num} {num === 1 ? 'bedroom' : 'bedrooms'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-[#111827]">Bedrooms needed</Label>
                    <Select
                      value={formData.needed_bedrooms}
                      onValueChange={(value) => setFormData({ ...formData, needed_bedrooms: value })}
                    >
                      <SelectTrigger className="bg-white border-gray-300 text-[#111827]" data-testid="needed-bedrooms-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {BEDROOM_OPTIONS.map((num) => (
                          <SelectItem key={num} value={num.toString()} className="text-[#111827]">
                            {num} {num === 1 ? 'bedroom' : 'bedrooms'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4: Postcode */}
            {step === 4 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#05D38A]/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#05D38A]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#111827]">What's your postcode district?</h3>
                    <p className="text-sm text-gray-500">This determines your local housing schemes</p>
                  </div>
                </div>
                
                <Select
                  value={formData.postcode_district}
                  onValueChange={(value) => setFormData({ ...formData, postcode_district: value })}
                >
                  <SelectTrigger className="bg-white border-gray-300 text-[#111827]" data-testid="postcode-select">
                    <SelectValue placeholder="Select postcode district" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 max-h-60">
                    <SelectItem value="header-london" disabled className="text-gray-400 font-semibold">
                      Greater London
                    </SelectItem>
                    {POSTCODES.London.map((code) => (
                      <SelectItem key={code} value={code} className="text-[#111827] pl-6">
                        {code} - London
                      </SelectItem>
                    ))}
                    <SelectItem value="header-birmingham" disabled className="text-gray-400 font-semibold">
                      Birmingham
                    </SelectItem>
                    {POSTCODES.Birmingham.map((code) => (
                      <SelectItem key={code} value={code} className="text-[#111827] pl-6">
                        {code} - Birmingham
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </motion.div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-3 pt-4">
              {step > 1 && (
                <Button
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  className="flex-1 border-gray-300 text-[#111827] hover:bg-gray-50"
                  data-testid="back-btn"
                >
                  Back
                </Button>
              )}
              
              {step < 4 ? (
                <Button
                  onClick={nextStep}
                  className="flex-1 bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold glow-mint"
                  data-testid="next-btn"
                >
                  Continue
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  className="flex-1 bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold glow-mint"
                  disabled={loading}
                  data-testid="complete-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-[#111827] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      Complete Setup
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
